import { LocationPinMap } from '@/components/app/location-pin-map';
import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { type ReactNode, useState } from 'react';

import {
  type ResolvedPlaceLink,
  useResolvePlaceLink,
} from '../api/resolve-place-link';

/** The fields of a saved branch the duplicate check needs. */
export interface KnownLocation {
  id: string;
  name: string | null;
  addressLine1: string;
  googlePlaceId: string | null;
}

interface MapsLinkLookupProps {
  disabled?: boolean;
  /** The org's branches, to spot the same Google place being added twice. */
  knownLocations: KnownLocation[];
  /** The branch being edited, which may of course match itself. */
  currentLocationId?: string;
  /** The owner confirmed this place — fill the form with it. */
  onUse: (place: ResolvedPlaceLink) => void;
  /** Failed plain-address lookup can keep the owner's input unchanged. */
  onUseRawAddress?: (address: string) => void;
}

const CLOSED_NOTES: Record<string, string> = {
  CLOSED_PERMANENTLY: 'Google lists this business as permanently closed.',
  CLOSED_TEMPORARILY: 'Google lists this business as temporarily closed.',
};

/**
 * "Paste a Google Maps link" for the location editor (ENG-1302).
 *
 * Deliberately a LOOKUP, not an import: the link is read into a card showing
 * the name, address and pin Google has for it, and nothing touches the form
 * until the owner presses "Use this place". The address fields below stay the
 * manual fallback — this only fills them, and they stay editable after.
 */
export function MapsLinkLookup({
  disabled = false,
  knownLocations,
  currentLocationId,
  onUse,
  onUseRawAddress,
}: MapsLinkLookupProps) {
  const [link, setLink] = useState('');
  const { resolvePlaceLink, place, error, isResolving, reset } =
    useResolvePlaceLink();

  const lookUp = () => {
    const value = link.trim();
    if (!value || isResolving) return;
    resolvePlaceLink(value);
  };

  const duplicate = place?.placeId
    ? knownLocations.find(
        (location) =>
          location.id !== currentLocationId &&
          location.googlePlaceId === place.placeId
      )
    : undefined;
  const closedNote = place?.businessStatus
    ? CLOSED_NOTES[place.businessStatus]
    : undefined;

  const use = () => {
    if (!place || duplicate) return;
    onUse(place);
    setLink('');
    reset();
  };

  return (
    <Field>
      <FieldLabel htmlFor="location-maps-link">
        Google Maps link, address or coordinates
      </FieldLabel>
      <div className="flex gap-2">
        <Input
          id="location-maps-link"
          placeholder="Maps link, address or 53.3493381, -6.2475564"
          value={link}
          disabled={disabled}
          onChange={(event) => {
            setLink(event.target.value);
            // Detach a pending lookup too: its result belongs to the old link.
            reset();
          }}
          onKeyDown={(event) => {
            // Enter looks the link up; it must not submit the whole editor.
            if (event.key === 'Enter') {
              event.preventDefault();
              lookUp();
            }
          }}
        />
        <Button
          type="button"
          variant="outline"
          disabled={disabled || isResolving || !link.trim()}
          onClick={lookUp}
        >
          {isResolving ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-label="Looking up" />
          ) : (
            'Look up'
          )}
        </Button>
      </div>
      {error ? (
        <div className="space-y-2">
          <p role="alert" className="text-destructive text-sm">
            {error}
          </p>
          {onUseRawAddress &&
          !/^(?:[a-z][a-z\d+.-]*:|www\.|<|\s*[+-]?\d+(?:\.\d+)?\s*[,\s]\s*[+-]?\d)/i.test(
            link.trim()
          ) ? (
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              onClick={() => {
                onUseRawAddress(link.trim());
                setLink('');
                reset();
              }}
            >
              Use address as entered
            </Button>
          ) : null}
        </div>
      ) : null}

      {place ? (
        <div
          className="space-y-3 rounded-md border p-3"
          data-testid="maps-link-result"
        >
          <LocationPinMap
            latitude={place.latitude}
            longitude={place.longitude}
          />
          <div className="min-w-0">
            <p className="font-medium">{place.name || 'Unnamed place'}</p>
            {place.addressFromGoogle ? (
              <p className="text-muted-foreground text-sm">
                {place.formattedAddress}
              </p>
            ) : !place.placeId ? (
              <p className="text-muted-foreground text-sm">
                Google couldn’t find an address for this pin. You can use the
                coordinates as entered and edit the address below.
              </p>
            ) : (
              <p className="text-muted-foreground text-sm">
                Your link gives us the exact pin. Google doesn’t put the street
                address in the link, so type it in the fields below after using
                this place.
              </p>
            )}
          </div>

          {duplicate ? (
            <Note>
              Already added as “{duplicate.name || duplicate.addressLine1}”.
              Pick a different place, or edit that location instead.
            </Note>
          ) : null}
          {closedNote ? <Note>{closedNote}</Note> : null}
          {place.addressFromGoogle && place.country === null ? (
            <Note>
              We can’t store Google’s country for this place — choose it below
              after using it.
            </Note>
          ) : null}

          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={reset}>
              Not this one
            </Button>
            <Button
              type="button"
              disabled={disabled || Boolean(duplicate)}
              onClick={use}
            >
              Use this place
            </Button>
          </div>
        </div>
      ) : null}
    </Field>
  );
}

function Note({ children }: { children: ReactNode }) {
  return (
    <p className="flex items-start gap-2 text-amber-700 text-sm dark:text-amber-400">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{children}</span>
    </p>
  );
}
